import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import {
  Stack,
  Button,
  Box,
  Alert,
  Checkbox,
  FormControlLabel,
  Tab,
  Tabs,
} from "@mui/material";
import { useAppDispatch } from "../../../store";
import { fetchPlayersWithRegistrations } from "../../../features/player/playerSlice";
import {
  listenToMatchs,
  createMatch,
} from "../../../features/matchs/matchsSlice";
import { showNotification } from "../../../features/globalNotification/globalNotificationSlice";
import BoardSelector from "./BoardSelector";
import RoundSelector from "./RoundSelector";
import MatchPlayerSelector from "./PlayerSelector";
import MatchsList from "./MatchsList";
import SpiddDraftsPanel from "./SpiddDrafts/SpiddDraftsPanel";
import { Matchs, Players } from "../../../types";
import { BasePlayerSelectorHandle } from "../../Base/BasePlayerSelector";
import {
  readAdminMatchsCreationForm,
  writeAdminMatchsCreationForm,
} from "./adminMatchsFormStorage";

const PLAYER_SLOT_COUNT = 3;

const AdminMatchs: FC = () => {
  const dispatch = useAppDispatch();
  const storedForm = readAdminMatchsCreationForm();

  const [tab, setTab] = useState(0);
  const [currentBoardId, setCurrentBoardId] = useState<string>(
    storedForm.boardId
  );
  const [currentRoundId, setCurrentRoundId] = useState<string>(
    storedForm.roundId
  );
  const [playersIds, setPlayersIds] = useState<(string | null)[]>([
    null,
    null,
    null,
  ]);
  const [error, setError] = useState<string | null>(null);
  const [showAllPlayers, setShowAllPlayers] = useState(false);
  const [highlightedMatchId, setHighlightedMatchId] = useState<string | null>(
    null
  );

  const playerSelectorRefs = useRef<Array<BasePlayerSelectorHandle | null>>([]);
  const createButtonRef = useRef<HTMLButtonElement>(null);

  const canCreate =
    Boolean(currentBoardId) &&
    Boolean(currentRoundId) &&
    !playersIds.every((id) => id === null);

  const focusPlayerSelector = useCallback((index: number) => {
    window.requestAnimationFrame(() => {
      playerSelectorRefs.current[index]?.focus();
    });
  }, []);

  const focusCreateButton = useCallback(() => {
    window.requestAnimationFrame(() => {
      createButtonRef.current?.focus();
    });
  }, []);

  const setPlayerSelectorRef = useCallback(
    (index: number) => (instance: BasePlayerSelectorHandle | null) => {
      playerSelectorRefs.current[index] = instance;
    },
    []
  );

  useEffect(() => {
    dispatch(fetchPlayersWithRegistrations());
    const unsubscribe = dispatch(listenToMatchs());
    return unsubscribe;
  }, [dispatch]);

  useEffect(() => {
    writeAdminMatchsCreationForm(currentBoardId, currentRoundId);
  }, [currentBoardId, currentRoundId]);

  useEffect(() => {
    if (!highlightedMatchId) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setHighlightedMatchId(null);
    }, 1800);
    return () => window.clearTimeout(timeoutId);
  }, [highlightedMatchId]);

  const handlePlayerSelect = (index: number, player: Players | null) => {
    setPlayersIds((prev) =>
      prev.map((pid, idx) => (idx === index ? player?.id ?? null : pid))
    );

    if (!player) {
      return;
    }

    if (index < PLAYER_SLOT_COUNT - 1) {
      focusPlayerSelector(index + 1);
      return;
    }

    focusCreateButton();
  };

  const handleCreateMatch = async () => {
    setError(null);

    if (!currentBoardId || !currentRoundId) {
      setError("Veuillez sélectionner un tableau et un tour");
      return;
    }

    if (playersIds.every((id) => id === null)) {
      setError("Veuillez sélectionner au moins un joueur");
      return;
    }

    const newMatch: Matchs = {
      dateCreation: new Date(),
      status: "CREE",
      tableauId: currentBoardId,
      tourId: currentRoundId,
      matchsPlayerParticipations: playersIds
        .filter((id) => id !== null)
        .map((id, idx) => ({
          ordre: idx + 1,
          playersId: id!,
        })),
    };

    try {
      const { matchId, status } = await dispatch(
        createMatch({ match: newMatch })
      ).unwrap();
      setPlayersIds([null, null, null]);
      setHighlightedMatchId(matchId);
      if (status === "DOUBLON") {
        dispatch(
          showNotification({
            message:
              "Match créé en DOUBLON : au moins un joueur est déjà en match.",
            severity: "warning",
            autoHideDuration: 3000,
          })
        );
      }
      focusPlayerSelector(0);
    } catch {
      setError("Erreur lors de la création du match");
    }
  };

  const handleCreateFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (canCreate) {
      void handleCreateMatch();
    }
  };

  return (
    <Stack spacing={2} direction="column">
      <Tabs
        value={tab}
        onChange={(_, value: number) => setTab(value)}
        textColor="primary"
        indicatorColor="primary"
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          minHeight: 44,
          "& .MuiTab-root": {
            color: "text.secondary",
            minHeight: 44,
            textTransform: "none",
            fontWeight: 500,
            "&.Mui-selected": {
              color: "text.primary",
              fontWeight: 700,
            },
          },
        }}
      >
        <Tab label="Matchs" />
        <Tab label="Propositions SPIDD" />
      </Tabs>

      {tab === 1 ? (
        <SpiddDraftsPanel />
      ) : (
        <>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Stack direction="row" alignItems="center" spacing={2}>
            <BoardSelector
              value={currentBoardId}
              handleBoardChange={setCurrentBoardId}
            />
            <RoundSelector
              value={currentRoundId}
              handleRoundChange={setCurrentRoundId}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showAllPlayers}
                  onChange={(_, checked) => setShowAllPlayers(checked)}
                  color="primary"
                />
              }
              label="Afficher tous les joueurs inscrits à au moins un tableau"
              sx={{
                ml: 2,
                whiteSpace: "nowrap",
                color: "text.primary",
                "& .MuiFormControlLabel-label": {
                  color: "text.primary",
                },
              }}
            />
          </Stack>

          <Box
            component="form"
            onSubmit={handleCreateFormSubmit}
            sx={{ width: "100%" }}
          >
            <Stack direction="row" alignItems="stretch" spacing={2} width="100%">
              {playersIds.map((playerId, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: 1,
                    minWidth: 180,
                    display: "flex",
                    alignItems: "stretch",
                  }}
                >
                  <MatchPlayerSelector
                    ref={setPlayerSelectorRef(index)}
                    label={`Joueur ${index + 1}`}
                    currentPlayerId={playerId}
                    currentBoardId={currentBoardId}
                    showAllPlayers={showAllPlayers}
                    onPlayerSelect={(player) => handlePlayerSelect(index, player)}
                  />
                </Box>
              ))}
              <Box
                sx={{
                  flex: "0 0 140px",
                  ml: 2,
                  display: "flex",
                  alignItems: "stretch",
                }}
              >
                <Button
                  ref={createButtonRef}
                  type="submit"
                  variant="contained"
                  disabled={!canCreate}
                  className="match-action-button"
                  sx={{
                    height: "56px",
                    width: "100%",
                    fontWeight: 600,
                    borderRadius: 2,
                    alignSelf: "stretch",
                  }}
                >
                  Créer un match
                </Button>
              </Box>
            </Stack>
          </Box>

          <MatchsList highlightedMatchId={highlightedMatchId} />
        </>
      )}
    </Stack>
  );
};

export default AdminMatchs;
